import React, { useEffect, useState } from "react";
import Print from "@mui/icons-material/Print";
import { useTranslations } from "hooks/useTranslations";
import { useSearchParams } from "react-router-dom";
import { useFetchConfirmQuery } from "redux/services/confirm";

import { subtitleAnimations, titleAnimations } from "utils/sharedAnimations";
import {
  Logo,
  IBookingPassenger,
  MediaQuery,
  IFlightCardTranslations,
  CustomAnimation,
  FlightCardSkeleton,
  useConfig,
} from "@thekilroygroup/shared-ui";
import {
  Box,
  Container,
  Typography,
  Divider,
  Button,
  Alert,
  SxProps,
  Stack,
  useTheme,
  Collapse,
  Link,
} from "@mui/material";
import { getFlightCardTranslations } from "utils/translationsUtils";
import {
  ContactPersonCard,
  PassengerCard,
  ReferenceCard,
  PriceSummaryCard,
  TravelDetailsCard,
  BookingNotFound,
  backgroundAnimations,
  fadeInCard,
  headerAnimations,
} from "components/Confirmation";
import { MobileHeader, DesktopHeader } from "components/Header";
import { searchQuerySelector } from "redux/searchQuery";
import { useSelector } from "react-redux";
import { MetapartnerTracking } from "components/MetapartnerTracking/MetapartnerTracking";
import { IAppConfigObject } from "appConfig.interfaces";
import { IConfirmation } from "../../components/Confirmation/PriceSummaryCard";
import { BannerAd } from "@thekilroygroup/shared-ui/dist/components/BannerAd";
import HotelIcon from "@mui/icons-material/Hotel";
import { Link as RouterLink } from "react-router-dom";
import { getCookie } from "utils/cookieDisclaimer";

export const ConfirmationPage = () => {
  const [confirmQueryParams] = useSearchParams();
  const { appConfig } = useConfig<IAppConfigObject>();
  const theme = useTheme();

  const { translations } = useTranslations();
  const flightCardTranslations = getFlightCardTranslations(translations);

  const [polling, setPolling] = useState(true);
  const [pollingCount, setPollingCount] = useState(0);
  const maxPollingCount = 5;

  const confirmationToken = getCookie("confirmationToken");

  const { data, isFetching, isError } = useFetchConfirmQuery(
    {
      query: confirmQueryParams.get("pnr"),
      config: appConfig,
      token: confirmationToken,
    },
    {
      pollingInterval: polling ? 1000 : 0,
    },
  );

  useEffect(() => {
    if ((!isError && response) || pollingCount >= maxPollingCount) {
      setPolling(false);
      return;
    } else if (isFetching) {
      setPollingCount((prev) => prev + 1);
    }
  }, [isFetching]);

  const {
    descriptionText,
    descriptionText2,
    descriptionText3,
    hotelBuyup,
    invoiceDetails,
    notificationText,
    paymentDetails,
    printButtonText,
    title,
    travelDetails,
  } = translations.confirmationPage;
  const response: IConfirmation = data;

  const shouldMount = isFetching || response;
  const searchQuery = useSelector(searchQuerySelector);

  const {
    container,
    textWrapper,
    header,
    description,
    printBtn,
    columnWrapper,
    columnHeader,
    columnOne,
    columnTwo,
    divider,
    column,
    printBtnWrapper,
    infoText,
  } = sxStyles;

  return (
    <Stack sx={{ backgroundColor: "grey.100" }}>
      <CustomAnimation {...headerAnimations} in={true}>
        <MediaQuery max="md">
          <MobileHeader
            justifyContent="space-around"
            top={<Logo height={20} color={theme.palette.white.main} />}
          />
        </MediaQuery>
        <MediaQuery min="md">
          <DesktopHeader />
        </MediaQuery>
      </CustomAnimation>
      <Container sx={container} maxWidth="lg">
        <CustomAnimation
          {...backgroundAnimations}
          in={shouldMount}
          mountOnEnter={true}
          unmountOnExit={true}
        >
          {!isError && response && (
            <Box sx={textWrapper}>
              <CustomAnimation {...titleAnimations} in={shouldMount}>
                <Typography
                  sx={header}
                  color={theme.palette.primary.main}
                  variant="h1"
                >
                  {title}
                </Typography>
              </CustomAnimation>

              <CustomAnimation {...subtitleAnimations} in={shouldMount}>
                <Box>
                  <Typography sx={description} variant="bodyMedium">
                    {descriptionText.replace(
                      "{email}",
                      response.contactPerson.email,
                    )}
                  </Typography>
                  <Typography sx={description} variant="bodyMedium">
                    {descriptionText2.replace(
                      "{email}",
                      response.contactPerson.email,
                    )}
                  </Typography>
                  <Typography sx={description} variant="bodyMedium">
                    {descriptionText3.replace(
                      "{email}",
                      response.contactPerson.email,
                    )}
                  </Typography>
                </Box>
              </CustomAnimation>

              <CustomAnimation
                {...subtitleAnimations}
                in={shouldMount}
                delay={300}
              >
                <Box>
                  <MediaQuery max="md">
                    <Typography
                      variant="bodyMedium"
                      sx={{ ...description, ...infoText } as SxProps}
                    >
                      {notificationText}
                    </Typography>
                  </MediaQuery>
                  <MediaQuery min="md">
                    <Alert
                      sx={{ mt: 3, justifyContent: "space-around" }}
                      variant="neutral"
                      icon={false}
                    >
                      <Typography variant="bodySmall">
                        {notificationText}
                      </Typography>
                    </Alert>
                    <Box sx={printBtnWrapper} className="printBtn">
                      <Button
                        sx={printBtn}
                        variant="contained"
                        size="large"
                        endIcon={<Print />}
                        onClick={() => {
                          window.print();
                        }}
                      >
                        {printButtonText}
                      </Button>
                    </Box>
                  </MediaQuery>
                </Box>
              </CustomAnimation>
              <Divider variant="fullWidth" sx={divider} />
            </Box>
          )}
          <Collapse in={polling || !isError}>
            <Stack sx={columnWrapper}>
              <Box sx={{ ...column, ...columnOne } as SxProps}>
                <CustomAnimation {...fadeInCard} in={true} delay={300}>
                  {!isError && response?.contactPerson ? (
                    <>
                      <ContactPersonCard contactPerson={response.contactPerson}>
                        <Typography sx={columnHeader} variant="h4">
                          {invoiceDetails.title}
                        </Typography>
                      </ContactPersonCard>
                    </>
                  ) : (
                    <FlightCardSkeleton elevation={2} />
                  )}
                </CustomAnimation>

                {!isError &&
                  response?.passengers &&
                  response.passengers.map(
                    (passenger: IBookingPassenger, i: number) => {
                      return (
                        <Box sx={{ mt: 2 }} key={`traveler-car-${i}`}>
                          <CustomAnimation
                            {...fadeInCard}
                            in={true}
                            delay={300}
                          >
                            <PassengerCard passenger={passenger} />
                          </CustomAnimation>
                        </Box>
                      );
                    },
                  )}
                <CustomAnimation {...fadeInCard} in={true} delay={300}>
                  {!isError && response?.pnr ? (
                    <ReferenceCard pnr={response.pnr} />
                  ) : (
                    <FlightCardSkeleton elevation={2} />
                  )}
                </CustomAnimation>
              </Box>
              <Box sx={{ ...column, ...columnTwo } as SxProps}>
                <CustomAnimation {...fadeInCard} in={true} delay={300}>
                  {!isError && response ? (
                    <PriceSummaryCard confirmation={response}>
                      <Typography sx={columnHeader} variant="h4">
                        {paymentDetails.title}
                      </Typography>
                    </PriceSummaryCard>
                  ) : (
                    <FlightCardSkeleton elevation={2} />
                  )}

                  <Box sx={{ ...column, mt: 2 }}>
                    {!isError && response?.itinerary ? (
                      <BannerAd
                        title={hotelBuyup.title}
                        text={hotelBuyup.text}
                        backgroundColor="grey.300"
                        icon={<HotelIcon />}
                        linkComponent={
                          <Link
                            component={RouterLink}
                            to={hotelBuyup.linkUrl}
                            target="_blank"
                          >
                            {hotelBuyup.buttonText}
                          </Link>
                        }
                      />
                    ) : (
                      <FlightCardSkeleton elevation={2} />
                    )}
                  </Box>
                </CustomAnimation>
              </Box>
            </Stack>
            <Divider variant="fullWidth" sx={divider} />
            <Box sx={column}>
              {!isError && response?.itinerary ? (
                <TravelDetailsCard
                  itinerary={response.itinerary}
                  flightCardTranslations={
                    flightCardTranslations as IFlightCardTranslations
                  }
                >
                  <Typography sx={columnHeader} variant="h4">
                    {travelDetails.title}
                  </Typography>
                </TravelDetailsCard>
              ) : (
                <FlightCardSkeleton elevation={2} />
              )}
            </Box>
          </Collapse>
        </CustomAnimation>
        {!isFetching && !response && pollingCount >= maxPollingCount && (
          <BookingNotFound mountDelay={1} />
        )}
      </Container>
      {!isError && response && searchQuery.utm_source && (
        <MetapartnerTracking
          selectedItinerary={data.itinerary}
          eventType="conversion"
          createdDate={new Date().toISOString()}
          utm_source={searchQuery.utm_source}
          conversion_id={response.id}
          totalPaidAmount={response.payment.totalPaid}
          paymentMethod={response.selectedPaymentMethod.method}
          fee={response.payment.fee}
          searchQuery={searchQuery}
          price={response.payment.totalPaid}
        />
      )}
    </Stack>
  );
};

const sxStyles: { [key: string]: SxProps } = {
  container: {
    flexGrow: "1",
    flexDirection: "column",
    justifyContent: "center",
    p: { xs: 2, md: 3 },
    pb: 3,
  },
  textWrapper: {
    p: { xs: 2, md: 0 },
    textAlign: "center",
  },
  header: {
    textAlign: "center",
    m: { xs: 3, md: 4 },
  },
  description: {
    my: 1,
  },
  infoText: {
    px: 0,
    pt: 5,
    pb: 2,
  },
  notification: {
    my: 2,
  },
  printBtnWrapper: {
    mt: 3,
    display: "flex",
    justifyContent: "end",
  },
  divider: {
    my: 3,
    width: "100%",
    display: { xs: "none", md: "initial" },
  },
  columnWrapper: {
    flexDirection: { xs: "column", md: "row" },
    gap: 3,
  },
  columnOne: {
    flexGrow: 3,
  },
  columnTwo: {
    flexGrow: 2,
  },
  columnHeader: {
    mb: 2,
    mt: { md: 1 },
  },
};
